import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Search, Building2, MapPin, Home, X, Loader2, List, 
  Map as MapIcon, Euro, FileText, CheckCircle2, TrendingUp, 
  Percent, ShieldCheck
} from 'lucide-react';
import { Property, PropertyStatus, InvestmentStrategy } from '../types';
import { useAuth } from '../hooks/useAuth';
import { usePropertiesQuery, useAddPropertyMutation, useUpdatePropertyMutation, useDeletePropertyMutation } from '../hooks/useAppQuery';
import PropertyMap from './PropertyMap';
import LocationAutocomplete from './LocationAutocomplete';

// Form Validation
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { propertySchema, PropertyFormData } from '../lib/schemas';

interface PropertiesManagerProps {
  lang: 'en' | 'pt';
}

const MotionDiv = motion.div as any;

const PropertiesManager: React.FC<PropertiesManagerProps> = ({ lang }) => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');

  // --- DATA HOOKS ---
  const { data: properties = [], isLoading, isError } = usePropertiesQuery(user?.id);
  const addMutation = useAddPropertyMutation(user?.id);
  const updateMutation = useUpdatePropertyMutation(user?.id);
  const deleteMutation = useDeletePropertyMutation(user?.id);

  // --- FORM SETUP ---
  const { 
    register, 
    handleSubmit, 
    reset, 
    setValue,
    control, 
    formState: { errors, isSubmitting } 
  } = useForm<PropertyFormData>({
    resolver: zodResolver(propertySchema) as any,
    defaultValues: {
      status: PropertyStatus.Vacant,
      strategy: InvestmentStrategy.BuyAndHold,
      purchasePrice: 0,
      monthlyRent: 0,
      expenses: 0,
      currentValue: 0,
      loanAmount: 0,
      interestRate: 0
    }
  });

  const handleOpenModal = (property?: Property) => {
    if (property) {
      setEditingId(property.id);
      setValue('address', property.address);
      setValue('city', property.city);
      setValue('zipCode', property.zipCode || '');
      setValue('purchasePrice', property.purchasePrice);
      setValue('currentValue', property.currentValue);
      setValue('monthlyRent', property.monthlyRent);
      setValue('expenses', property.expenses);
      setValue('status', property.status);
      setValue('strategy', property.strategy);
      setValue('imageUrl', property.imageUrl || '');
      setValue('loanAmount', property.loanAmount || 0);
      setValue('interestRate', property.interestRate || 0);
      setValue('insuranceExpiry', property.insuranceExpiry || '');
      // Recuperar coordenadas se existirem
      if (property.lat) setValue('lat', property.lat);
      if (property.lng) setValue('lng', property.lng);
    } else {
      setEditingId(null);
      reset();
    }
    setIsModalOpen(true);
  };

  const onSubmit = async (data: PropertyFormData) => {
    try {
      if (editingId) {
        await updateMutation.mutateAsync({ ...data, id: editingId } as Property);
      } else {
        await addMutation.mutateAsync(data as unknown as Property);
      }
      setIsModalOpen(false);
      reset();
    } catch (error) {
      console.error("Failed to save property", error);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm(lang === 'pt' ? 'Tem a certeza?' : 'Are you sure?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const filteredProperties = properties.filter(p => 
    p.address.toLowerCase().includes(searchTerm.toLowerCase()) || 
    p.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-emerald-500 w-8 h-8"/></div>;

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-8 pb-24 h-full flex flex-col">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 shrink-0">
        <div>
          <h2 className="text-3xl font-bold text-white tracking-tight">{lang === 'pt' ? 'Portfólio' : 'Portfolio'}</h2>
          <p className="text-slate-400 mt-1">{filteredProperties.length} {lang === 'pt' ? 'imóveis geridos' : 'properties managed'}</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-900 rounded-xl p-1 border border-slate-700">
            <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-emerald-500 text-white shadow' : 'text-slate-400 hover:text-white'}`}>
              <List className="w-4 h-4" />
            </button>
            <button onClick={() => setViewMode('map')} className={`p-2 rounded-lg transition-all ${viewMode === 'map' ? 'bg-emerald-500 text-white shadow' : 'text-slate-400 hover:text-white'}`}>
              <MapIcon className="w-4 h-4" />
            </button>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input 
              type="text" 
              placeholder={lang === 'pt' ? "Procurar..." : "Search..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500 w-48 md:w-64 transition-all"
            />
          </div>
          <button onClick={() => handleOpenModal()} className="bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 shadow-lg shadow-emerald-500/20 transition-all hover:scale-105 active:scale-95">
            <Plus className="w-4 h-4" /> <span className="hidden md:inline">{lang === 'pt' ? 'Adicionar' : 'Add Property'}</span>
          </button>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="flex-1 min-h-0">
        {viewMode === 'map' ? (
          <div className="h-[600px] w-full rounded-2xl overflow-hidden border border-slate-800 bg-slate-900/50 backdrop-blur shadow-2xl relative">
             <PropertyMap properties={filteredProperties} onSelectProperty={(p) => handleOpenModal(p)} />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-20">
            <AnimatePresence mode="popLayout">
              {filteredProperties.map((property) => (
                <MotionDiv
                  key={property.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="group bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl overflow-hidden hover:border-emerald-500/30 transition-all hover:shadow-2xl hover:shadow-emerald-900/10 cursor-pointer"
                  onClick={() => handleOpenModal(property)}
                >
                  <div className="h-48 bg-slate-800 relative overflow-hidden">
                    {property.imageUrl ? (
                      <img src={property.imageUrl} alt={property.address} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-700"><Building2 className="w-12 h-12" /></div>
                    )}
                    <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-md px-3 py-1 rounded-full text-xs font-medium text-white border border-white/10">
                      {property.status}
                    </div>
                  </div>

                  <div className="p-5">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-white leading-tight">{property.address}</h3>
                        <div className="flex items-center gap-1.5 text-slate-400 text-sm mt-1">
                          <MapPin className="w-3.5 h-3.5" /> {property.city}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-5">
                      <div className="bg-slate-950/50 p-3 rounded-xl border border-slate-800/50">
                          <p className="text-[10px] uppercase text-slate-500 font-bold tracking-wider mb-1">Value</p>
                          <p className="text-emerald-400 font-mono font-medium">€{property.currentValue.toLocaleString()}</p>
                      </div>
                      <div className="bg-slate-950/50 p-3 rounded-xl border border-slate-800/50">
                          <p className="text-[10px] uppercase text-slate-500 font-bold tracking-wider mb-1">Rent</p>
                          <p className="text-blue-400 font-mono font-medium">€{property.monthlyRent.toLocaleString()}<span className="text-slate-600 text-[10px]">/mo</span></p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-800">
                        <span className="text-xs text-slate-500 font-medium bg-slate-800 px-2 py-1 rounded">{property.strategy}</span>
                        <div className="flex gap-2">
                            <button onClick={(e) => { e.stopPropagation(); handleDelete(property.id); }} className="text-xs text-red-400 hover:text-red-300 px-3 py-1.5 rounded-lg hover:bg-red-500/10 transition-colors">Delete</button>
                        </div>
                    </div>
                  </div>
                </MotionDiv>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* NEW/EDIT PROPERTY MODAL */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <MotionDiv 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-slate-950 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl"
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-slate-950 border-b border-slate-800 p-6 flex justify-between items-center z-20 shrink-0 rounded-t-2xl">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                     <Home className="w-5 h-5 text-emerald-500"/>
                     {editingId ? (lang === 'pt' ? 'Editar Imóvel' : 'Edit Property') : (lang === 'pt' ? 'Novo Imóvel' : 'New Property')}
                  </h3>
                  <p className="text-xs text-slate-400 mt-1">Fill in the details below to track your asset.</p>
                </div>
                <button onClick={() => setIsModalOpen(false)} className="text-slate-500 hover:text-white transition-colors p-2 hover:bg-slate-800 rounded-full"><X className="w-6 h-6"/></button>
              </div>
              
              {/* Modal Body (Scrollable) */}
              <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-8 overflow-y-auto flex-1 custom-scrollbar">
                
                {/* 1. Location */}
                <div className="space-y-4">
                   <h4 className="text-xs font-bold text-emerald-500 uppercase tracking-widest flex items-center gap-2">
                      <MapPin className="w-4 h-4"/> Location Details
                   </h4>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5 md:col-span-2">
                         <label className="text-xs font-bold text-slate-300">Full Address</label>
                         
                         {/* CONTROLLER DO AUTOCOMPLETE */}
                         <Controller
                            control={control}
                            name="address"
                            render={({ field: { onChange, value } }) => (
                              <LocationAutocomplete 
                                initialValue={value}
                                onInputChange={onChange}
                                onLocationSelect={(data) => {
                                  onChange(data.address);
                                  setValue('city', data.city, { shouldValidate: true });
                                  if (data.zipCode) setValue('zipCode', data.zipCode);
                                  // Fix Coordenadas
                                  if (data.lat) setValue('lat', data.lat);
                                  if (data.lng) setValue('lng', data.lng);
                                }}
                                placeholder={lang === 'pt' ? "Ex: Rua da Liberdade, Lisboa..." : "Start typing to search..."}
                              />
                            )}
                         />
                         
                         {/* Inputs escondidos para coordenadas */}
                         <input type="hidden" {...register('lat')} />
                         <input type="hidden" {...register('lng')} />

                         {errors.address && <span className="text-xs text-red-400 block mt-1">{errors.address.message as string}</span>}
                      </div>
                      
                      <div className="space-y-1.5">
                         <label className="text-xs font-bold text-slate-400">City</label>
                         <input {...register('city')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white focus:border-emerald-500 outline-none transition-all" />
                      </div>
                      <div className="space-y-1.5">
                         <label className="text-xs font-bold text-slate-400">Zip Code</label>
                         <input {...register('zipCode')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white focus:border-emerald-500 outline-none transition-all" />
                      </div>
                   </div>
                </div>

                <div className="h-px bg-slate-800/50 w-full" />

                {/* 2. Financials */}
                <div className="space-y-4">
                   <h4 className="text-xs font-bold text-emerald-500 uppercase tracking-widest flex items-center gap-2">
                      <Euro className="w-4 h-4"/> Asset Performance
                   </h4>
                   <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-slate-900/30 p-4 rounded-xl border border-slate-800/50">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">Purchase Price</label>
                        <div className="relative">
                           <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">€</span>
                           <input type="number" {...register('purchasePrice')} className="w-full bg-slate-900 border border-slate-700 rounded-lg py-2 pl-7 pr-2 text-white text-sm focus:border-emerald-500 outline-none" />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-emerald-400 uppercase">Current Value</label>
                        <div className="relative">
                           <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">€</span>
                           <input type="number" {...register('currentValue')} className="w-full bg-slate-900 border border-slate-700 rounded-lg py-2 pl-7 pr-2 text-white text-sm focus:border-emerald-500 outline-none" />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-blue-400 uppercase">Monthly Rent</label>
                        <div className="relative">
                           <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">€</span>
                           <input type="number" {...register('monthlyRent')} className="w-full bg-slate-900 border border-slate-700 rounded-lg py-2 pl-7 pr-2 text-white text-sm focus:border-emerald-500 outline-none" />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-red-400 uppercase">Expenses</label>
                        <div className="relative">
                           <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">€</span>
                           <input type="number" {...register('expenses')} className="w-full bg-slate-900 border border-slate-700 rounded-lg py-2 pl-7 pr-2 text-white text-sm focus:border-emerald-500 outline-none" />
                        </div>
                      </div>
                   </div>
                </div>

                {/* 3. Financing (CAMPOS NOVOS) */}
                <div className="space-y-4">
                   <h4 className="text-xs font-bold text-emerald-500 uppercase tracking-widest flex items-center gap-2">
                      <TrendingUp className="w-4 h-4"/> Financing & Debt
                   </h4>
                   <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-slate-400">Loan Amount (€)</label>
                        <input type="number" {...register('loanAmount')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white focus:border-emerald-500 outline-none" />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-slate-400">Interest Rate (%)</label>
                        <div className="relative">
                           <input type="number" step="0.01" {...register('interestRate')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white focus:border-emerald-500 outline-none pr-8" />
                           <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                        </div>
                      </div>
                   </div>
                </div>

                <div className="h-px bg-slate-800/50 w-full" />

                {/* 4. Strategy & Details */}
                <div className="space-y-4">
                   <h4 className="text-xs font-bold text-emerald-500 uppercase tracking-widest flex items-center gap-2">
                      <FileText className="w-4 h-4"/> Strategy & Compliance
                   </h4>
                   <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                         <label className="text-xs font-bold text-slate-400">Status</label>
                         <select {...register('status')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:border-emerald-500 cursor-pointer">
                            {Object.values(PropertyStatus).map(s => <option key={s} value={s}>{s}</option>)}
                         </select>
                      </div>
                      <div className="space-y-1.5">
                         <label className="text-xs font-bold text-slate-400">Strategy</label>
                         <select {...register('strategy')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:border-emerald-500 cursor-pointer">
                            {Object.values(InvestmentStrategy).map(s => <option key={s} value={s}>{s}</option>)}
                         </select>
                      </div>
                      <div className="space-y-1.5">
                         <label className="text-xs font-bold text-slate-400 flex items-center gap-1"><ShieldCheck className="w-3 h-3"/> Insurance Expiry</label>
                         <input type="date" {...register('insuranceExpiry')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white focus:border-emerald-500 outline-none calendar-picker-indicator-white" />
                      </div>
                      <div className="space-y-1.5">
                         <label className="text-xs font-bold text-slate-400">Image URL</label>
                         <input {...register('imageUrl')} placeholder="https://..." className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white focus:border-emerald-500 outline-none" />
                      </div>
                   </div>
                </div>

                <div className="h-32"></div>
              </form>

              {/* Modal Footer */}
              <div className="p-6 border-t border-slate-800 bg-slate-950 shrink-0 flex justify-end gap-3 rounded-b-2xl">
                  <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">Cancel</button>
                  <button onClick={handleSubmit(onSubmit)} disabled={isSubmitting} className="px-6 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-bold flex items-center gap-2 disabled:opacity-50 shadow-lg shadow-emerald-500/20 transition-all active:scale-95">
                      {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>}
                      {lang === 'pt' ? 'Guardar Imóvel' : 'Save Property'}
                  </button>
              </div>
            </MotionDiv>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PropertiesManager;